import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query'; // Added useQueryClient
import { toast } from 'react-toastify';
import Flatpickr from 'react-flatpickr';
import {
    delete_stock_out,
    get_all_filters_item_stock_out_new,
} from '../api/stocksApi';
import { get_all_department } from '../../departments/api/departmentsApi'; // Assuming this fetches all for dropdown
import { get_all_users as getAllUsersForFilter } from '../../users/api/usersApi'; // Renamed for clarity, assuming it fetches users for dropdown
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { formatDateFlatpickr, formatDateWithoutTime, formatDateWithTime } from '../../../helper-functions/DateFunction';
import { KTIcon } from '../../../../_metronic/helpers';

// --- Type Definitions ---
type StockOut = {
    id: number;
    stock_type?: string;
    department_id: number | undefined;
    department_name: string | undefined;
    user_id: number | undefined;
    user_name: string | undefined;
    date_time: string;
    updated_by_name: string | undefined;
    updated_at: string;
};

interface DepartmentType { // For filter dropdown
    id: number;
    name: string | undefined;
};
interface UserType { // For filter dropdown
    id: number;
    full_name: string | undefined; // Or firstname, lastname if API returns that
};

type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

type ApiStockOutDataPayload = {
    stocks: StockOut[]; // Assuming API returns 'stocks' array
    pagination: ApiPaginationInfo;
    links: { /* ... link structure ... */ };
};

type ApiStockOutFullResponse = {
    data: { // From Axios
        success: boolean;
        message: string;
        data: ApiStockOutDataPayload; // From your API structure
    };
};
// --- End Type Definitions ---


var stock_out_add: boolean, stock_out_edit: boolean, stock_out_view: boolean, stock_out_logs: boolean, stock_out_delete: boolean;

function StockOutList() {
    const [stockOutList, setStockOutList] = useState<StockOut[]>([]); // For current page

    // Filter States
    const [searchInput, setSearchInput] = useState<string>('');
    const [selectedUser, setSelectedUser] = useState<string>(''); // Store as string for select
    const [selectedDepartment, setSelectedDepartment] = useState<string>(''); // Store as string for select
    const [fromDateFilter, setFromDateFilter] = useState<string>('');
    const [toDateFilter, setToDateFilter] = useState<string>('');

    // State for API filter payload
    const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
        fromDateFilter: '',
        toDateFilter: '',
        selectedDepartment: '',
        selectedUser: '',
        // searchTerm: '', // If backend supports search
    });

    // Pagination State
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
    const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);

    // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // For server-side sorting

    const [selectedStockOut, setSelectedStockOut] = useState<StockOut | null>(null);
    const [showAllDepartment, setShowAllDepartment] = useState<DepartmentType[]>([]);
    const [showAllUser, setShowAllUser] = useState<UserType[]>([]);

    const fromDateRef = useRef<Flatpickr>(null);
    const toDateRef = useRef<Flatpickr>(null);
    // const [delayedLoading, setDelayedLoading] = useState(true); // Handled by react-query

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    stock_out_add = permissionCheck('stock-create');
    stock_out_edit = permissionCheck('stock-update');
    stock_out_view = permissionCheck('stock-view');
    stock_out_logs = permissionCheck('stock-activity-log');
    stock_out_delete = permissionCheck('stock-delete');

    // Fetch Departments for filter dropdown
    const { data: AllDepartments } = useQuery('allDepartmentsForStockOutFilter', get_all_department, {
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
            const departments = response?.data?.data || response?.data;
            if (departments && Array.isArray(departments)) {
                setShowAllDepartment(departments as DepartmentType[]);
            }
        }
    });
    const { data: AllUsers } = useQuery(
        'allUsersForStockOutFilter',
        () => getAllUsersForFilter(),
        {
            refetchOnWindowFocus: false,
            onSuccess: (response) => {
                // Adjust based on actual response structure of getAllUsersForFilter
                const users = response?.data?.data?.users || response?.data?.data || response?.data;
                if (users && Array.isArray(users)) {
                    setShowAllUser(users as UserType[]);
                }
            }
        }
    );


    // --- Main Query for Filtered and Paginated Stock Out Records ---
    const {
        isLoading,
        isFetching,
        isError,
        error,
    } = useQuery<ApiStockOutFullResponse, Error>(
        ['filtered_stock_out', filterPayloadForApi, currentPage, itemsPerPage],
        () => get_all_filters_item_stock_out_new(
            filterPayloadForApi.fromDateFilter,
            filterPayloadForApi.toDateFilter,
            filterPayloadForApi.selectedDepartment,
            filterPayloadForApi.selectedUser,
            currentPage,
            itemsPerPage
            // filterPayloadForApi.searchTerm, // If server-side search
        ),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            onSuccess: (response) => {
                const apiPayload = response.data.data.stocks;

                if (apiPayload && Array.isArray(apiPayload)) {
                    setStockOutList(apiPayload);

                    const pagination = response.data.data.pagination;
                    if (pagination) {
                        setTotalItems(pagination.total);
                        setTotalPages(pagination.last_page);
                        setCurrentPage(pagination.current_page);
                        setCurrentRangeFrom(pagination.from);
                        setCurrentRangeTo(pagination.to);
                    } else {
                        // Handle case where pagination is missing
                        setTotalItems(0);
                        setTotalPages(0);
                        setCurrentRangeFrom(1);
                        setCurrentRangeTo(apiPayload.length || 0);
                    }
                } else {
                    setStockOutList([]);
                    setTotalItems(0);
                    setTotalPages(0);
                    setCurrentRangeFrom(1);
                    setCurrentRangeTo(0);
                }
            },
            onError: (error: any) => {
                toast.error(error.message || "Failed to fetch stock out records.", { autoClose: 3000 });
                setStockOutList([]);
                setTotalItems(0);
                setTotalPages(0);
            },
        }
    );

    // Client-side search (optional)
    const displayedStockOutList = searchInput
        ? stockOutList.filter((stock) =>
            stock.user_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
            stock.department_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
            formatDateWithoutTime(stock.date_time).includes(searchInput.toLowerCase()) // Search formatted date
        )
        : stockOutList;

    // Mutation for deleting stock out record
    const handleDeleteStockOut = useMutation(
        (id: number) => delete_stock_out(id),
        {
            onSuccess: (response) => {
                toast.success(response.message || "Stock Out record deleted successfully.", { autoClose: 3000 });
                queryClient.invalidateQueries(['filtered_stock_out', filterPayloadForApi, currentPage, itemsPerPage]);
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || error.message || "Failed to delete Stock Out record.";
                toast.error(errorMessage, { autoClose: 3000 });
            },
        }
    );

    const handleApplyFilters = () => {
        setCurrentPage(1);
        setFilterPayloadForApi({
            fromDateFilter: fromDateFilter,
            toDateFilter: toDateFilter,
            selectedDepartment: selectedDepartment,
            selectedUser: selectedUser,
            // searchTerm: searchInput, // If server-side search
        });
    };

    const handleClearFilters = () => {
        if (fromDateRef.current) fromDateRef.current.flatpickr.clear();
        if (toDateRef.current) toDateRef.current.flatpickr.clear();
        setFromDateFilter('');
        setToDateFilter('');
        setSelectedDepartment('');
        setSelectedUser('');
        setSearchInput('');

        setCurrentPage(1);
        setFilterPayloadForApi({
            fromDateFilter: '', toDateFilter: '', selectedDepartment: '', selectedUser: '', /* searchTerm: '' */
        });
    };

    // Date picker handlers
    const handleDateFromChange = (selectedDates: Date[]) => {
        if (selectedDates && selectedDates[0]) {
            const formatted = formatDateFlatpickr(selectedDates[0]);
            setFromDateFilter(formatted);
            if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', selectedDates[0]);
        } else {
            setFromDateFilter('');
            if (toDateRef.current) toDateRef.current.flatpickr.set('minDate', null);
        }
    };
    const handleDateToChange = (selectedDates: Date[]) => {
        if (selectedDates && selectedDates[0]) {
            const formatted = formatDateFlatpickr(selectedDates[0]);
            setToDateFilter(formatted);
        } else {
            setToDateFilter('');
        }
    };

    // Pagination handlers
    const itemsPerPageOptions = [10, 20, 30, 50, 100];
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // Navigation handlers
    const handleStockOutLogs = (stock: StockOut) => navigate("/stocks/stock-out-logs", { state: { id: stock.id } });
    const handleEditStockOut = (stock: StockOut) => navigate("/stocks/edit-stock-out", { state: { stock: stock } }); // Pass full object if needed
    const handleViewStockOut = (stock: StockOut) => navigate("/stocks/view-stock-out", { state: { id: stock.id } });

    // Removed client-side sorting. Server-side sorting needed.

    if (isLoading && !isFetching && currentPage === 1) {
        return <div className='d-flex justify-content-center align-items-center' style={{ height: '200px' }}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
    }

    return (
        <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
            {/* Card Header */}
            <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>Stock Out List</span>
                    <span className='text-muted mt-1 fw-semibold fs-7'>
                        {totalItems > 0 ? `${totalItems} records found` : 'No records'}
                    </span>
                </h3>
                <div className='card-toolbar'>
                    {stock_out_add && (
                        <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/stocks/add-stock-out')}>
                            <KTIcon iconName='plus' className='fs-3' /> Add Stock Out
                        </button>
                    )}
                </div>
            </div>

            {/* Filters Section */}
            <div className='card-body pt-0'>
                <div className='row mb-4 align-items-center gy-3'>
                    <div className='col-md-3'>
                        <div className='input-group'>
                            <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
                            <input
                                type='text'
                                className='form-control form-control-solid'
                                placeholder='Search user, dept, date...'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='col-md-2'>
                        <Flatpickr
                            ref={fromDateRef}
                            onChange={handleDateFromChange}
                            className='form-control form-control-solid'
                            placeholder='From Date'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                    <div className='col-md-2'>
                        <Flatpickr
                            ref={toDateRef}
                            onChange={handleDateToChange}
                            className='form-control form-control-solid'
                            placeholder='To Date'
                            options={{ dateFormat: 'd/m/Y' }}
                        />
                    </div>
                    <div className='col-md-2'>
                        <select
                            className='form-select form-select-solid'
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                        >
                            <option value=''>Department...</option>
                            {showAllDepartment.map((dept) => (
                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className='col-md-3'> {/* Adjusted width for user select and buttons */}
                        <div className="d-flex align-items-center">
                            <select
                                className='form-select form-select-solid me-2' // Added me-2 for spacing
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                            >
                                <option value=''>User...</option>
                                {showAllUser.map((user) => (
                                    <option key={user.id} value={user.id}>{user.full_name}</option>
                                ))}
                            </select>
                            <button className='btn btn-light-danger me-2' onClick={handleClearFilters} disabled={isFetching} style={{ whiteSpace: 'nowrap' }}>
                                <KTIcon iconName='cross' className='fs-5 me-1' /> Clear
                            </button>
                            <button className='btn btn-primary' onClick={handleApplyFilters} disabled={isFetching} style={{ whiteSpace: 'nowrap' }}>
                                <KTIcon iconName='filter-search' className='fs-5 me-1' /> Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>


            {/* Table Section */}
            <div className='card-body py-3'>
                {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating stock out list...</i></div>}
                <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                            <tr className='fw-bold text-muted bg-light'>
                                <th className='ps-4 min-w-150px rounded-start'>Department</th>
                                <th className='min-w-150px'>User</th>
                                <th className='min-w-120px'>Date</th>
                                <th className='min-w-150px'>Updated By</th>
                                <th className='min-w-150px'>Updated At</th>
                                {(stock_out_delete || stock_out_edit || stock_out_logs || stock_out_view) &&
                                    <th className='min-w-150px text-center rounded-end'>Actions</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && currentPage === 1 ? (
                                <tr><td colSpan={6} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
                            ) : isError ? (
                                <tr><td colSpan={6} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
                            ) : displayedStockOutList.length > 0 ? (
                                displayedStockOutList.map((stock) => (
                                    <tr key={stock.id}>
                                        <td className='ps-4'>
                                            <span className='text-dark fw-bold text-hover-primary d-block fs-6 cursor-pointer' onClick={() => handleViewStockOut(stock)}>
                                                {stock.department_name}
                                            </span>
                                        </td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{stock.user_name}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithoutTime(stock.date_time)}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{stock.updated_by_name || 'N/A'}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(stock.updated_at)}</span></td>
                                        {(stock_out_delete || stock_out_edit || stock_out_logs || stock_out_view) &&
                                            <td className='text-center'>
                                                <div className='d-flex justify-content-center flex-shrink-0'>
                                                    {stock_out_view && (<button onClick={() => handleViewStockOut(stock)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                                                    {stock_out_edit && (<button onClick={() => handleEditStockOut(stock)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                                                    {stock_out_logs && (<button onClick={() => handleStockOutLogs(stock)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                                                    {stock_out_delete && (<button onClick={() => setSelectedStockOut(stock)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_stock_out'><KTIcon iconName='trash' className='fs-3' /></button>)}
                                                </div>
                                            </td>
                                        }
                                    </tr>
                                ))
                            ) : (
                                !isFetching && <tr><td colSpan={6} className="text-center py-5">No stock out records found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer: Pagination and Items Per Page */}
            {totalItems > 0 && (
                <div className='card-footer d-flex justify-content-between align-items-center py-4'>
                    <div>
                        <span className='text-muted me-2'>Showing:</span>
                        <select
                            className='form-select form-select-sm d-inline-block w-auto me-3'
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isFetching}
                        >
                            {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
                        </select>
                        <span className='text-muted'>
                            {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} records
                        </span>
                    </div>

                    <nav>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
                            </li>
                            {/* Optional: Page Number Logic */}
                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <div className="modal fade" tabIndex={-1} id="kt_modal_delete_stock_out">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Stock Out Confirmation</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this stock out record? <br />
                                For department: <strong>{selectedStockOut?.department_name}</strong>,
                                by user: <strong>{selectedStockOut?.user_name}</strong>,
                                dated: <strong>{selectedStockOut ? formatDateWithoutTime(selectedStockOut.date_time) : ''}</strong>.
                            </p>
                            <p className='text-muted small'>This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => { if (selectedStockOut) handleDeleteStockOut.mutate(selectedStockOut.id); }}
                                data-bs-dismiss="modal"
                                disabled={handleDeleteStockOut.isLoading}
                            >
                                {handleDeleteStockOut.isLoading ? 'Deleting...' : 'Delete Stock Out'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StockOutList;